/**
 * useTutorLiveRoomDrop — une image glissée n'importe où dans la salle, ou collée hors d'un champ, arrive dans le
 * brouillon de génération (Wajih, 04/11 : « les quatre entrées doivent être ultra fluides »).
 *
 * Au web seulement, écouté sur le document :
 *  - **le glisser** : dès qu'un fichier passe au-dessus de la fenêtre, toute la salle devient la zone de dépôt
 *    (`dropping`, que la page dessine) ; lâché, le fichier part au brouillon ;
 *  - **le collage** : une image collée alors qu'aucun champ n'a le focus. Un collage dans un champ (le chat, une
 *    réponse) reste à ce champ ; celui du brouillon a sa propre écoute (`listenOnField`), qui le marque traité.
 *
 * `enabled` faux (le chat ouvert, le centre seul) : rien n'est écouté — le chat garde ses propres pièces. Un fichier
 * lâché d'un autre type qu'une image ou un PDF n'est pas ignoré en silence : `refuse` le dit.
 */
import { useEffect, useRef, useState } from "react"

import { IS_WEB } from "../../../../../../engine/shared/platform"
import { type UIActivityFile } from "../../../types"
import { droppedFile, pastedImage } from "../../../../view-models/tutor/Live/tutorLiveFeedRules"
import { isEditableTarget } from "../../../../../../design-system/utils/shutterKey"

/** Le fichier d'un collage ou d'un dépôt, sous l'adresse que le navigateur lui donne (`URL.createObjectURL`). */
export function webFile(file: File): UIActivityFile {
    return { uri: URL.createObjectURL(file), name: file.name, mimeType: file.type }
}

/** Le glisser porte des fichiers (pas un texte, pas un lien). */
function carriesFiles(event: DragEvent): boolean {
    const types = event.dataTransfer?.types
    if (types == null) return false
    for (let i = 0; i < types.length; i++) if (types[i] === "Files") return true
    return false
}

export function useTutorLiveRoomDrop(
    receive: (file: UIActivityFile, via: "drop" | "paste") => void,
    enabled: boolean,
    refuse?: () => void
): { dropping: boolean } {
    const [dropping, setDropping] = useState(false)
    const receiveRef = useRef(receive)
    receiveRef.current = receive
    const refuseRef = useRef(refuse)
    refuseRef.current = refuse

    useEffect(() => {
        if (!IS_WEB || !enabled) {
            setDropping(false)
            return undefined
        }
        const doc = (globalThis as { document?: Document }).document
        if (doc == null) return undefined
        // Les entrées et sorties d'un glisser se comptent : quitter un enfant de la salle n'est pas la quitter.
        let depth = 0
        const onEnter = (event: DragEvent) => {
            if (!carriesFiles(event)) return
            depth += 1
            setDropping(true)
        }
        const onOver = (event: DragEvent) => {
            if (carriesFiles(event)) event.preventDefault()
        }
        const onLeave = (event: DragEvent) => {
            if (!carriesFiles(event)) return
            depth = Math.max(0, depth - 1)
            if (depth === 0) setDropping(false)
        }
        const onDrop = (event: DragEvent) => {
            depth = 0
            setDropping(false)
            if (event.defaultPrevented || !carriesFiles(event)) return
            // Un fichier lâché dans la salle ne quitte jamais la salle (le navigateur l'ouvrirait à sa place).
            event.preventDefault()
            const file = droppedFile(event.dataTransfer?.files)
            if (file != null) receiveRef.current(webFile(file), "drop")
            else refuseRef.current?.()
        }
        const onPaste = (event: ClipboardEvent) => {
            if (event.defaultPrevented || isEditableTarget(event.target)) return
            const image = pastedImage(event.clipboardData as unknown as Parameters<typeof pastedImage>[0])
            if (image == null) return
            event.preventDefault()
            receiveRef.current(webFile(image), "paste")
        }
        doc.addEventListener("dragenter", onEnter)
        doc.addEventListener("dragover", onOver)
        doc.addEventListener("dragleave", onLeave)
        doc.addEventListener("drop", onDrop)
        doc.addEventListener("paste", onPaste)
        return () => {
            doc.removeEventListener("dragenter", onEnter)
            doc.removeEventListener("dragover", onOver)
            doc.removeEventListener("dragleave", onLeave)
            doc.removeEventListener("drop", onDrop)
            doc.removeEventListener("paste", onPaste)
        }
    }, [enabled])

    return { dropping }
}
